/* ===================================================================
   Shared UI primitives for the Rhody Reviews app screens.
   Ported from the Claude Design handoff app/screens.js helper functions
   (stars, chip, ava, kpi, review, alertRow, funnel, source, etc.) into
   typed, reusable React components. Consumed by Dashboard, Reviews, and
   later screens. Styling lives in styles/components.css (token-based).

   HARD PRODUCT RULE honored here: a drafted reply NEVER auto-posts.
   ReviewCard's "Approve & post" button is the ONLY post action and only
   fires on an explicit click (onApprove). There is no auto-send path.
   =================================================================== */
import type { CSSProperties, ReactNode } from 'react';

/* ---------------- inline icons (stroke, ported from the design) -------- */
export function Icon({ d, sw = 2 }: { d: ReactNode; sw?: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}>
      {d}
    </svg>
  );
}
export const CheckPath = (
  <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
);
export const SearchPath = (
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" strokeLinecap="round" />
  </>
);
export const ClosePath = <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />;
export const PlusPath = <path d="M12 5v14M5 12h14" strokeLinecap="round" />;

/* Star brandmark glyph (the cobalt-square star, NO crown -- per handoff).
   Reused by empty states / first-run so the visual matches BrandMark. */
export function BrandGlyph({ size = 48 }: { size?: number }) {
  const inner = Math.round(size * 0.46);
  return (
    <span className="brandglyph" style={{ width: size, height: size }}>
      <svg viewBox="0 0 16 16" fill="#fff" width={inner} height={inner}>
        <path d="M8 1.5l2 4.5 5 .5-3.7 3.4 1 4.9L8 12l-4.3 2.8 1-4.9L1 6.5 6 6z" />
      </svg>
    </span>
  );
}

/* ---------------- Stars ---------------- */
export function Stars({ n, low }: { n: number; low?: boolean }) {
  return (
    <span className={'stars' + (low ? ' low' : '')}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 16 16"
          fill={i < n ? 'currentColor' : 'rgba(255,255,255,.15)'}
        >
          <path d="M8 1l2 4.5 5 .4-3.8 3.3 1.1 4.8L8 11.6 3.7 14l1.1-4.8L1 5.9l5-.4z" />
        </svg>
      ))}
    </span>
  );
}

/* ---------------- Switch (toggle) ----------------
   Controlled or uncontrolled toggle. Used by Widget, FeedbackGate,
   Automations, and Settings. Visual only here; persisting the value is a
   TODO wired through api.ts (Hopper). */
export function Switch({
  label,
  checked,
  defaultChecked,
  onChange,
  disabled,
}: {
  label?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  const controlled = checked !== undefined;
  return (
    <label className="switch" style={disabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}>
      <input
        type="checkbox"
        disabled={disabled}
        {...(controlled ? { checked } : { defaultChecked })}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="track" />
      {label != null && <span style={{ fontSize: 13.5 }}>{label}</span>}
    </label>
  );
}

/* ---------------- Chip ---------------- */
export type ChipKind = 'green' | 'blue' | 'gold' | 'red' | 'gray';
export function Chip({
  kind = 'gray',
  children,
  style,
}: {
  kind?: ChipKind;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <span className={'chip ' + kind} style={style}>
      {children}
    </span>
  );
}

/* ---------------- Avatar ---------------- */
export function Avatar({
  initial,
  color,
  className,
}: {
  initial: string;
  color: string;
  className?: string;
}) {
  return (
    <span className={'ava' + (className ? ' ' + className : '')} style={{ background: color }}>
      {initial}
    </span>
  );
}

/* ---------------- Card ---------------- */
export function Card({
  className,
  style,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div className={'card' + (className ? ' ' + className : '')} style={style}>
      {children}
    </div>
  );
}
export function CardHead({
  title,
  link,
  onLink,
  right,
  style,
}: {
  title: string;
  link?: string;
  onLink?: () => void;
  right?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div className="card__head" style={style}>
      <h3>{title}</h3>
      {link && (
        <span className="link" onClick={onLink}>
          {link}
        </span>
      )}
      {right}
    </div>
  );
}

/* ---------------- KPI ---------------- */
export type Delta = 'up' | 'down' | 'flat';
export function Kpi({
  label,
  delta,
  dir = 'flat',
  accent,
  children,
}: {
  label: string;
  delta: string;
  dir?: Delta;
  // Brand-color the big number. Defaults to the primary UI blue when omitted;
  // pass one of the Google accents to vary the KPI row (used sparingly).
  accent?: 'blue' | 'green' | 'gold' | 'red' | 'ink';
  children: ReactNode;
}) {
  return (
    <Card className="kpi">
      <div className="lab">{label}</div>
      <div className={'num' + (accent ? ' num--' + accent : '')}>{children}</div>
      <div className={'delta ' + dir}>
        {dir === 'up' ? '▲ ' : dir === 'down' ? '▼ ' : ''}
        {delta}
      </div>
    </Card>
  );
}

/* ---------------- Sparkline ----------------
   Tiny inline trend drawn from an already-fetched numeric series (no new
   fetch). Null points render as gaps (zero height), mirroring the chart
   discipline elsewhere. Decorative on its own; pass `caption` so a
   screen-reader gets the meaning (redundant encoding, not color/shape
   alone). Used beside subscriber KPI numbers. */
export function Sparkline({
  values,
  tone = 'blue',
  caption,
  height = 22,
}: {
  values: (number | null)[];
  tone?: 'blue' | 'green' | 'gold';
  caption?: string;
  height?: number;
}) {
  const nums = values.filter((v): v is number => v != null);
  if (nums.length < 2) return null; // not enough to draw a trend honestly
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const span = max - min || 1;
  const lastReal = values.reduce((acc, v, i) => (v != null ? i : acc), -1);
  return (
    <span className={'kpi-spark kpi-spark--' + tone} style={{ height }} aria-hidden="true">
      {values.map((v, i) => {
        const h = v == null ? 0 : Math.max(2, Math.round(((v - min) / span) * (height - 2)) + 2);
        return <i key={i} className={i === lastReal ? 'is-last' : undefined} style={{ height: h }} />;
      })}
      {caption && <span className="visually-hidden">{caption}</span>}
    </span>
  );
}

/* ---------------- Skeleton loaders ----------------
   Shaped grey placeholders so a slow first load reserves the layout instead
   of popping in. Pure presentation; no data, no side effects. */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div className={'sk' + (className ? ' ' + className : '')} style={style} aria-hidden="true" />;
}
/* A KPI tile in its loading state -- mirrors <Kpi> sizing. */
export function SkeletonKpi() {
  return (
    <div className="card kpi sk-kpi" aria-hidden="true">
      <div className="sk sk-lab" />
      <div className="sk sk-num" />
      <div className="sk sk-delta" />
    </div>
  );
}
/* A chart-card body in its loading state. */
export function SkeletonChart() {
  return <div className="sk sk-chart" aria-hidden="true" />;
}
/* A few stat rows (label + bar) in their loading state. */
export function SkeletonStats({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div className="sk sk-stat" style={{ width: `${70 - i * 8}%` }} />
          <div className="sk sk-stat--bar" style={{ width: `${85 - i * 12}%` }} />
        </div>
      ))}
    </div>
  );
}

/* ---------------- Stat row + Progress ---------------- */
export function Progress({ pct, color }: { pct: number; color?: string }) {
  return (
    <div className="progress">
      <i style={{ width: pct + '%', ...(color ? { background: color } : {}) }} />
    </div>
  );
}
export function Stat({
  name,
  value,
  dotColor,
  pct,
}: {
  name: string;
  value: ReactNode;
  dotColor?: string;
  pct?: number;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="stat" style={{ border: 0, padding: '0 0 6px' }}>
        <span className="l">
          {dotColor && (
            <span
              style={{
                display: 'inline-block',
                width: 9,
                height: 9,
                borderRadius: 3,
                background: dotColor,
                marginRight: 8,
              }}
            />
          )}
          {name}
        </span>
        <span className="v">{value}</span>
      </div>
      {pct != null && <Progress pct={pct} color={dotColor} />}
    </div>
  );
}

/* ---------------- Toolbar + Segmented ---------------- */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}
export type Segment = { label: string; badge?: string };
export function Segmented({
  items,
  active,
  onChange,
}: {
  items: Segment[];
  active: number;
  onChange: (i: number) => void;
}) {
  return (
    <div className="seg">
      {items.map((s, i) => (
        <button
          key={s.label}
          className={i === active ? 'is-active' : ''}
          onClick={() => onChange(i)}
        >
          {s.label}
          {s.badge && <span className="badge">{s.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------- AlertRow ---------------- */
export type AlertKind = 'warn' | 'info' | 'win';
const ALERT_ICON: Record<AlertKind, ReactNode> = {
  warn: (
    <>
      <path d="M12 3l9 16H3z" strokeLinejoin="round" />
      <path d="M12 10v4M12 17v.5" strokeLinecap="round" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8v.5" strokeLinecap="round" />
    </>
  ),
  win: CheckPath,
};
export function AlertRow({
  kind,
  title,
  desc,
  when,
}: {
  kind: AlertKind;
  title: string;
  desc: string;
  when: string;
}) {
  return (
    <div className={'alert ' + kind}>
      <span className="ico">
        <Icon d={ALERT_ICON[kind]} />
      </span>
      <div className="t">
        <b>{title}</b>
        <span>{desc}</span>
      </div>
      <span className="when">{when}</span>
    </div>
  );
}

/* ---------------- Funnel ---------------- */
export function Funnel({ rows }: { rows: { label: string; val: string; pctTxt: string; pct: number }[] }) {
  return (
    <div className="funnel">
      {rows.map((r) => (
        <div className="frow" key={r.label}>
          <div className="ft">
            <span>{r.label}</span>
            <b>
              {r.val} · {r.pctTxt}
            </b>
          </div>
          <Progress pct={r.pct} />
        </div>
      ))}
    </div>
  );
}

/* ---------------- ReviewCard ----------------
   Renders a single review with an optional AI-drafted reply and the
   Approve / Edit / Skip actions. The reply is a DRAFT; it is never posted
   until the operator clicks Approve & post (onApprove). No auto-send. */
export type ReviewItem = {
  id: string;
  /** The row's DB uuid (distinct from id = the platform review_id). Used by
      the manual-reply endpoint. */
  rowId?: string;
  initial: string;
  who: string;
  color: string;
  stars: number;
  source: string;
  when: string;
  text: string;
  /** AI-drafted reply, shown for human approval. Optional. */
  draft?: string;
  /** Pending draft has been flagged for owner attention. Shows an "Escalated" pill. */
  escalated?: boolean;
};
export function ReviewCard({
  review,
  showActions = true,
  onApprove,
  onEdit,
  onSkip,
}: {
  review: ReviewItem;
  showActions?: boolean;
  onApprove?: (id: string) => void;
  onEdit?: (id: string) => void;
  onSkip?: (id: string) => void;
}) {
  return (
    <div className="rev">
      <Avatar initial={review.initial} color={review.color} />
      <div className="body">
        <div className="top">
          <span className="who">{review.who}</span>
          <Stars n={review.stars} low={review.stars <= 2} />
          <span className="src">
            {review.source} · {review.when}
          </span>
          {review.escalated && (
            <Chip kind="gold" style={{ marginLeft: 'auto' }}>Escalated</Chip>
          )}
        </div>
        <div className="txt">{review.text}</div>

        {review.draft && (
          <div className="reply">
            <div className="tag">
              <svg viewBox="0 0 20 20" fill="currentColor" stroke="none">
                <path d="M10 1l2 5 5 1-4 4 1 5-4-2.5L6 16l1-5-4-4 5-1z" />
              </svg>
              AI draft · in your voice
            </div>
            {review.draft}
          </div>
        )}

        {showActions && (
          <div className="actions">
            {/* Approve & post is the ONLY action that posts, and only on
                this explicit click. Nothing auto-posts. */}
            <button className="mini mini--go" onClick={() => onApprove?.(review.id)}>
              <Icon d={CheckPath} sw={2.4} /> Approve &amp; post
            </button>
            <button className="mini mini--ghost" onClick={() => onEdit?.(review.id)}>
              Edit
            </button>
            <button className="mini mini--ghost" onClick={() => onSkip?.(review.id)}>
              Skip
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- EmptyState ----------------
   Reusable empty / zero-data treatment. Brand-glyph + headline + copy and an
   optional primary action. Used by first-run dashboards and any screen with
   no data yet (no reviews, no customers, no requests). Action is an explicit
   click handler only -- nothing here triggers a post or send on its own. */
export function EmptyState({
  title,
  desc,
  actionLabel,
  onAction,
  glyph = true,
  style,
}: {
  title: string;
  desc: string;
  actionLabel?: string;
  onAction?: () => void;
  glyph?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div className="emptybox" style={style}>
      {glyph && <BrandGlyph size={56} />}
      <h3>{title}</h3>
      <p>{desc}</p>
      {actionLabel && (
        <button className="btn btn--primary" style={{ margin: '0 auto' }} onClick={onAction}>
          <Icon d={PlusPath} sw={2.2} /> {actionLabel}
        </button>
      )}
    </div>
  );
}

/* ---------------- EmptyKpi ----------------
   A KPI tile in its zero/placeholder state (dim "-" or "0" figure). Mirrors
   <Kpi> sizing but with no delta line. Used by the first-run dashboard so the
   layout is fully formed before any data arrives. */
export function EmptyKpi({ label, value = '-' }: { label: string; value?: ReactNode }) {
  return (
    <div className="card ke">
      <div className="lab">{label}</div>
      <div className="num">{value}</div>
    </div>
  );
}

/* ---------------- Checklist (first-run setup) ----------------
   Day-one setup checklist with a progress bar. Each step renders its done/
   pending state; pending steps expose a labelled action link. onStep is an
   explicit click handler (navigation / open-flow) -- no side effects fire on
   render. */
export type ChecklistStep = {
  id: string;
  title: string;
  /** sub-line: count imported, etc. */
  sub: string;
  done?: boolean;
  /** action link label for a pending step, e.g. "Set up" / "Send". */
  action?: string;
};
export function Checklist({
  steps,
  label,
  onStep,
}: {
  steps: ChecklistStep[];
  label?: string;
  onStep?: (id: string) => void;
}) {
  const done = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  return (
    <div className="checklist">
      <div className="prlab">{label ?? 'Setup'} · {done} of {steps.length} complete</div>
      <Progress pct={pct} />
      <div style={{ height: 14 }} />
      {steps.map((s) => (
        <div key={s.id} className={'clitem' + (s.done ? ' done' : '')}>
          <span className="ckbox">{s.done && <Icon d={CheckPath} sw={3} />}</span>
          <div className="t">
            <b>{s.title}</b>
            <span>{s.sub}</span>
          </div>
          {!s.done && s.action && (
            <span className="go" onClick={() => onStep?.(s.id)}>
              {s.action} &rarr;
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
